import React, { useState } from 'react';
import useTranslate from '../../../../Hooks/useTranslate';
import { formatThousands } from '../../../../Utils/constants/general';
import {
  CenteredColumnFlexBox,
  ColumnFlexBox,
  FlexBox,
} from '../../../../Components/FlexBox';
import SectionHeaderPaper from './SectionHeaderPaper';
import SectionTotalsPaper from './SectionTotalsPaper';
import SectionCountersPaper from './SectionCountersPaper';
import SectionCallDetailsPaper from './SectionCallDetailsPaper';
import SectionDonutPaper from './SectionDonutPaper';
import AddIcCallOutlinedIcon from '@mui/icons-material/AddIcCallOutlined';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';

const toPercent = (part, total) =>
  total ? Math.round((part / total) * 100) : 0;

/**
 * For the TOTAL section, data comes from graphData directly:
 *   totalFiltersCount, contactableCount, totalFilterDetails, totalCallOutcomesDetails,
 *   totalCallCount, answeredTotalCount, unansweredTotalCount
 *
 * For per-section rows (targetCounters / notTargetCounters items):
 *   totalFiltersCount, contactableCount, filterDetails,
 *   callDetails.totalCount, callDetails.answeredTotalCount,
 *   callDetails.unansweredTotalCount, callDetails.outcomeCounters
 */
const ReportSectionWrapper = function ReportSectionWrapper({
  data,
  title,
  branchDescription,
  wavePeriod,
  isTotal = false,
}) {
  const translate = useTranslate();
  const [donutMode, setDonutMode] = useState('filters');

  if (!data) return null;

  const totalFiltersCount = data.totalFiltersCount;
  const filteredCount = data.filteredCount;
  const contactableCount = data.contactableCount;
  const filterDetails = isTotal
    ? (data.totalFilterDetails ?? [])
    : (data.filterDetails ?? []);
  const outcomeCounters = isTotal
    ? (data.totalCallOutcomesDetails ?? [])
    : (data.callDetails?.outcomeCounters ?? []);
  const totalCallCount = isTotal
    ? data.totalCallCount
    : data.callDetails?.callCount;
  const answeredTotalCount = isTotal
    ? data.answeredTotalCount
    : data.callDetails?.answeredCallCount;
  const unansweredTotalCount = isTotal
    ? data.unansweredTotalCount
    : data.callDetails?.unansweredCallCount;
  const uniqueContacts = isTotal
    ? data.uniqueTotalContacts
    : data.callDetails?.uniqueContacts;
  const uniqueAnsweredContacts = isTotal
    ? data.uniqueAnwseredTotalContacts
    : data.callDetails?.uniqueAnsweredContacts;
  const uniqueUnansweredContacts = isTotal
    ? data.uniqueUnansweredTotalContacts
    : data.callDetails?.uniqueUnansweredContacts;
  // Only contacts reached with at least one *valid* call are considered
  // "contacted"; this can be lower than the raw unique-contacts figure.
  const validUniqueContacts = isTotal
    ? data.validUniqueCallCount
    : data.callDetails?.validUniqueContacts;

  const toContact =
    validUniqueContacts > contactableCount
      ? 0
      : contactableCount - validUniqueContacts;
  // Contacts called but without any valid call: they are NOT subtracted, so
  // "to contact" can be higher than the naive (contactable - unique contacts).
  const invalidCalledContacts = Math.max(
    0,
    (uniqueContacts ?? 0) - (validUniqueContacts ?? 0)
  );

  const tooltipRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '24px',
  };
  const toContactTooltip = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={tooltipRowStyle}>
        <span>{translate('CONTACTABLE')}</span>
        <span>{formatThousands(contactableCount)}</span>
      </div>
      <div style={tooltipRowStyle}>
        <span>− {translate('VALID_UNIQUE_CONTACTS')}</span>
        <span>{formatThousands(validUniqueContacts)}</span>
      </div>
      <div
        style={{
          ...tooltipRowStyle,
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          paddingTop: '6px',
          fontWeight: 700,
        }}
      >
        <span>{translate('TO_CONTACT')}</span>
        <span>{formatThousands(toContact)}</span>
      </div>
      {invalidCalledContacts > 0 && (
        <span style={{ marginTop: '6px', opacity: 0.85 }}>
          {translate('TO_CONTACT_INVALID_HINT')} (
          {formatThousands(invalidCalledContacts)})
        </span>
      )}
    </div>
  );

  return (
    <CenteredColumnFlexBox gap="24px" padding="24px" width="90%">
      <SectionHeaderPaper
        title={title}
        branchDescription={branchDescription}
        wavePeriod={wavePeriod}
      />
      <FlexBox gap="24px" width="100%">
        <ColumnFlexBox gap="24px" width="35%" flexGrow={0}>
          <SectionTotalsPaper
            totalFiltersCount={totalFiltersCount}
            filteredCount={filteredCount}
            contactableCount={contactableCount}
            filterDetails={filterDetails}
            selected={donutMode === 'filters'}
            onClick={() => setDonutMode('filters')}
          />
          <SectionCallDetailsPaper
            rows={[
              {
                icon: <PhoneInTalkIcon sx={{ color: '#4fc3f7' }} />,
                label: translate('TOTAL_CALLS'),
                total: totalCallCount,
                unique: { value: uniqueContacts },
              },
              {
                icon: <PhoneCallbackIcon sx={{ color: '#81c784' }} />,
                label: translate('ANSWERED'),
                total: answeredTotalCount,
                unique: {
                  value: uniqueAnsweredContacts,
                  percentage: toPercent(uniqueAnsweredContacts, uniqueContacts),
                },
              },
              {
                icon: <PhoneMissedIcon sx={{ color: '#e57373' }} />,
                label: translate('UNANSWERED'),
                total: unansweredTotalCount,
                unique: {
                  value: uniqueUnansweredContacts,
                  percentage: toPercent(
                    uniqueUnansweredContacts,
                    uniqueContacts
                  ),
                },
              },
            ]}
            selected={donutMode === 'outcomes'}
            onClick={() => setDonutMode('outcomes')}
          />
          <SectionCountersPaper
            counters={[
              {
                icon: <AddIcCallOutlinedIcon sx={{ color: '#e67300' }} />,
                label: translate('TO_CONTACT'),
                value: toContact,
                tooltip: toContactTooltip,
              },
            ]}
          />
        </ColumnFlexBox>
        <SectionDonutPaper
          filterDetails={filterDetails}
          outcomeCounters={outcomeCounters}
          mode={donutMode}
        />
      </FlexBox>
    </CenteredColumnFlexBox>
  );
};

export default ReportSectionWrapper;
